import { ENDGAME_ZONES, ZONE_HUNT_MIN_LEVEL } from './balance-core.mjs';
import { DROP_ONLY_TM_TYPES } from './drop-only-items.mjs';

export const DROP_ONLY_POOL = Object.freeze({
  PHEROMONE: 'pheromone',
  TM: 'tm',
});

const TM_TYPE_LABELS = Object.freeze({
  electric: 'elétrico',
  fire: 'fogo',
  water: 'água',
  grass: 'planta',
  bug: 'inseto',
  flying: 'voador',
  normal: 'normal',
  psychic: 'psíquico',
  rock: 'pedra',
  ground: 'terra',
  ghost: 'fantasma',
  dragon: 'dragão',
  fighting: 'lutador',
  poison: 'veneno',
  dark: 'sombrio',
  steel: 'aço',
  ice: 'gelo',
  fairy: 'fada',
});

// Probability per regular encounter at the first endgame zone. Zone loot
// multipliers are baked into the generated rows so server and local rolls agree.
export const ENDGAME_DROP_BASE_CHANCE_PPM = Object.freeze({
  pheromone: 700,
  tm: 900,
});

function zoneForId(zoneId) {
  return ENDGAME_ZONES.find((zone) => zone.id === String(zoneId || '')) || null;
}

function freezeRow(row) {
  return Object.freeze({
    slug: row.slug,
    pool: row.pool,
    type: row.type || null,
    chancePpm: Math.max(1, Math.round(row.chancePpm)),
    weight: Math.max(1, Math.round(row.weight || 1)),
    minQty: Math.max(1, Math.round(row.minQty || 1)),
    maxQty: Math.max(Math.round(row.minQty || 1), Math.round(row.maxQty || 1)),
  });
}

function rowsForZone(zone) {
  const multiplier = Number(zone.effects?.lootMult) || 1;
  const pheroChance = Math.min(100000, Math.round(ENDGAME_DROP_BASE_CHANCE_PPM.pheromone * multiplier));
  const tmChance = Math.min(100000, Math.round(ENDGAME_DROP_BASE_CHANCE_PPM.tm * multiplier));
  const primaryWeight = 8 + zone.tier * 2;
  const pheromone = freezeRow({
    slug: 'pheromone',
    pool: DROP_ONLY_POOL.PHEROMONE,
    chancePpm: pheroChance,
    weight: 1,
    minQty: 1,
    maxQty: 1,
  });
  const tms = DROP_ONLY_TM_TYPES.map((type) => freezeRow({
    slug: `tm-${type}`,
    pool: DROP_ONLY_POOL.TM,
    type: TM_TYPE_LABELS[type],
    chancePpm: tmChance,
    weight: TM_TYPE_LABELS[type] === zone.primaryType ? primaryWeight : 1,
    minQty: 1,
    maxQty: 1,
  }));
  return Object.freeze([pheromone, ...tms]);
}

export const ENDGAME_ITEM_LOOT = Object.freeze(Object.fromEntries(
  ENDGAME_ZONES.map((zone) => [zone.id, rowsForZone(zone)]),
));

export function endgameLootRowsForZone(zoneId) {
  return ENDGAME_ITEM_LOOT[String(zoneId || '')] || Object.freeze([]);
}

export function endgameZoneIdForLevel(level) {
  const value = Number(level);
  if (!Number.isFinite(value) || value < ZONE_HUNT_MIN_LEVEL) return null;
  return ENDGAME_ZONES.find((zone) => zone.level === value)?.id || null;
}

function addQuantity(out, slug, qty) {
  out[slug] = (out[slug] || 0) + qty;
}

function rollWeighted(rows, random) {
  const total = rows.reduce((sum, row) => sum + row.weight, 0);
  if (total <= 0) return null;
  let cursor = Math.min(0.999999999, Math.max(0, Number(random()) || 0)) * total;
  for (const row of rows) {
    cursor -= row.weight;
    if (cursor < 0) return row;
  }
  return rows.at(-1) || null;
}

/**
 * Rolls only the zone-specific drop-only pool. The caller supplies the zone
 * from the authoritative encounter context; an invalid/pre-endgame context is
 * deliberately a no-op.
 */
export function rollEndgameDrops(zoneId, encounterLevel, random = Math.random, count = 1) {
  const zone = zoneForId(zoneId);
  const level = Number(encounterLevel);
  if (!zone || !Number.isFinite(level) || level < zone.level || level < ZONE_HUNT_MIN_LEVEL) return {};
  const rows = endgameLootRowsForZone(zone.id);
  const pheromone = rows.find((row) => row.pool === DROP_ONLY_POOL.PHEROMONE);
  const tms = rows.filter((row) => row.pool === DROP_ONLY_POOL.TM);
  const rolls = Math.max(1, Math.min(500, Math.floor(Number(count) || 1)));
  const out = {};
  for (let index = 0; index < rolls; index += 1) {
    if (pheromone && random() < pheromone.chancePpm / 100000) {
      addQuantity(out, pheromone.slug, pheromone.minQty);
    }
    const tmChance = tms[0]?.chancePpm || 0;
    if (tms.length && random() < tmChance / 100000) {
      const selected = rollWeighted(tms, random);
      if (selected) addQuantity(out, selected.slug, selected.minQty);
    }
  }
  return out;
}
